import { isToday } from 'date-fns'
import { cn, formatDateTimeHuman, formatTimeHuman } from '@/lib/utils'
import { Check, ChevronDown, ClipboardList, MapPin, X } from 'lucide-react'
import type { DiningTable, Order, TableSession } from '@/types'
import type { OrderTypeConfig } from '@/lib/orderTypeSettings'
import { displayLabelForOrderType } from '@/lib/orderTypeSettings'
import { orderTypePillLook, ticketLifecyclePillLook, type TicketLifecycleMeta } from './ticketState'

export interface TicketHeaderProps {
  orderType: string
  /** Resolved labels/colors for order-type pills */
  orderTypesConfig: OrderTypeConfig[]
  selectedTable: DiningTable | null
  existingOrder: Order | null
  dineInSession: TableSession | null
  customerName: string
  /** Live delivery destination (delivery orders) — shown under the identity row. */
  deliveryAddress?: string
  checkoutOpen: boolean
  lifecycle: TicketLifecycleMeta
  /**
   * Transient inline affirmation shown after items are fired to the kitchen.
   * Replaces the previous floating "Add-on sent" / "Sent to kitchen" toast,
   * which overlapped the Totals and primary CTA in the rail.
   */
  firedConfirmation?: { count: number; mode: 'new' | 'continue' } | null
  /** Tapping the persistent Table chip opens the transfer dialog. */
  onChangeTable?: () => void
  /** When provided, render the "New order" button that releases the loaded ticket. */
  onCloseOrder?: () => void
  /** Whether anything is currently "loaded" — controls "New order" visibility. */
  hasLoadedOrder?: boolean
}

/**
 * Slim ticket header. Used to host close-checkout and Move-table buttons in
 * the right-hand action stack; those actions now live in `CounterRailToolbar`
 * directly below this header so the header can stay one tight block of
 * identity (order type, lifecycle, table/customer, order #, guest meta).
 */
export function TicketHeader({
  orderType,
  orderTypesConfig,
  selectedTable,
  existingOrder,
  dineInSession,
  customerName,
  deliveryAddress,
  checkoutOpen,
  lifecycle,
  firedConfirmation,
  onChangeTable,
  onCloseOrder,
  hasLoadedOrder,
}: TicketHeaderProps) {
  const showTableIdentity = orderType === 'dine_in' && (selectedTable || existingOrder?.table)
  const tableNumber = selectedTable?.table_number ?? existingOrder?.table?.table_number
  const orderNumber = existingOrder?.order_number
  const rawGuests = dineInSession?.guestCount ?? existingOrder?.guest_count ?? null
  const guests = rawGuests != null && rawGuests > 0 ? rawGuests : null
  const serverName =
    dineInSession?.serverDisplayName ??
    (existingOrder?.user
      ? `${existingOrder.user.first_name ?? ''} ${existingOrder.user.last_name ?? ''}`.trim() ||
        existingOrder.user.username
      : undefined)

  // When the order was opened: the seated/tab anchor when the dine-in flow
  // recorded one, else the order row's creation time — so every loaded
  // order shows its placed time, not just open-tab dine-ins.
  const openedAt = existingOrder?.table_opened_at ?? existingOrder?.created_at

  // Delivery destination — prefer the persisted address once the order exists,
  // else the live draft so it shows while the cashier is still composing.
  const deliveryAddr =
    orderType === 'delivery'
      ? (existingOrder?.delivery_address || deliveryAddress || '').trim()
      : ''

  const secondaryBits: string[] = []
  if (guests != null) secondaryBits.push(`${guests} ${guests === 1 ? 'guest' : 'guests'}`)
  if (serverName) secondaryBits.push(`Server ${serverName}`)
  if (openedAt) {
    // The open-orders strip carries orders from previous business days;
    // a bare "10:35 PM" on a two-day-old ticket is misleading, so include
    // the date whenever the order wasn't opened today.
    const openedDate = new Date(openedAt)
    secondaryBits.push(
      `Opened ${isToday(openedDate) ? formatTimeHuman(openedDate) : formatDateTimeHuman(openedDate)}`
    )
  }

  // Suppress redundant helper text when it merely restates the lifecycle pill
  // ("Staging" + "Items not yet sent to the kitchen" was duplicative); keep
  // helpers that add real context (e.g. "Awaiting payment").
  const helperText = lifecycle.helper
  const helperRedundant =
    lifecycle.state === 'staging' &&
    helperText.toLowerCase().includes('not yet sent')
  const showHelper = Boolean(helperText) && !helperRedundant

  const otPill = orderTypePillLook(orderType, orderTypesConfig)
  const lifePill = ticketLifecyclePillLook(lifecycle.tone, orderType, orderTypesConfig)

  const showCloseButton = Boolean(onCloseOrder && hasLoadedOrder)
  const tableChipClickable = Boolean(onChangeTable && showTableIdentity && tableNumber && !checkoutOpen)

  return (
    <header
      aria-label="Ticket context"
      className="flex flex-col gap-1.5 border-b border-border/60 bg-card/95 px-4 py-2.5 backdrop-blur-sm sm:px-5"
    >
      <div className="flex items-start gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn(otPill.className)} style={otPill.style}>
              {displayLabelForOrderType(orderType, orderTypesConfig)}
            </span>
            <span className={cn(lifePill.className)} style={lifePill.style}>
              {lifecycle.label}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {showTableIdentity && tableNumber ? (
              tableChipClickable ? (
                <button
                  type="button"
                  onClick={onChangeTable}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-sm font-semibold tracking-tight hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={`Table ${tableNumber} — tap to transfer`}
                >
                  <span className="text-muted-foreground font-medium">Table</span>
                  {tableNumber}
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />
                </button>
              ) : (
                <span className="text-base font-semibold tracking-tight">
                  <span className="text-muted-foreground">Table </span>
                  {tableNumber}
                </span>
              )
            ) : customerName ? (
              <span className="truncate text-base font-semibold tracking-tight">
                {customerName}
              </span>
            ) : orderType !== 'dine_in' ? (
              <span className="text-sm text-muted-foreground">Walk-in customer</span>
            ) : null}
            {orderNumber && (
              <span className="text-sm text-muted-foreground">
                Order <span className="font-medium text-foreground">#{orderNumber}</span>
              </span>
            )}
          </div>
          {deliveryAddr && (
            <div className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{deliveryAddr}</span>
            </div>
          )}
          {secondaryBits.length > 0 && (
            <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
              {secondaryBits.map((bit, i) => (
                <span key={`${bit}-${i}`}>
                  {i > 0 ? <span aria-hidden>· </span> : null}
                  {bit}
                </span>
              ))}
            </div>
          )}
          {showHelper && (
            <div className="mt-0.5 text-xs text-muted-foreground">{helperText}</div>
          )}
        </div>
        {/* Release the ticket so the next order can start (F-FE-N144). Named
            for the outcome: `clearLoadedOrder` settles nothing, so the old
            "Close this order" read as cancelling the bill and collided with
            the footer's close-checkout. Hidden below the split, where the tab
            bar carries it instead so it is reachable from the Menu tab too —
            complementary CSS rather than a JS media query, so there is never a
            frame with neither control on screen. */}
        {showCloseButton && (
          <button
            type="button"
            onClick={onCloseOrder}
            className="-mr-1 hidden min-h-11 shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:inline-flex"
            title="Close the current order and start a new one"
          >
            <X className="h-4 w-4" aria-hidden />
            New order
          </button>
        )}
      </div>
      {existingOrder && !checkoutOpen && existingOrder.kot_first_sent_at && (
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <ClipboardList className="h-3.5 w-3.5" aria-hidden />
          <span>Items have been fired to the kitchen</span>
        </div>
      )}
      {firedConfirmation && (
        <div
          role="status"
          aria-live="polite"
          className="counter-fired-chip inline-flex w-fit items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800 shadow-sm dark:border-emerald-900/60 dark:bg-emerald-950/60 dark:text-emerald-200"
        >
          <Check className="h-3.5 w-3.5" aria-hidden />
          <span>
            {firedConfirmation.count} {firedConfirmation.count === 1 ? 'item' : 'items'}{' '}
            {firedConfirmation.mode === 'continue' ? 'added to kitchen' : 'sent to kitchen'}
          </span>
        </div>
      )}
    </header>
  )
}
